import { getToken } from "./kinde";

async function authHeader(): Promise<HeadersInit> {
  const t = await getToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const headers = { ...(init.headers || {}), ...(await authHeader()) };
  const r = await fetch(path, { ...init, headers });
  if (!r.ok) {
    const detail = await r.text().catch(() => "");
    throw new Error(`${r.status} ${r.statusText}: ${detail}`);
  }
  return r.json() as Promise<T>;
}

export type Visitor = {
  ip: string;
  country: string;
  region: string;
  city: string;
  description: string;
  fly_region_header: string;
};

export type Environment = "prod" | "stable";

export type AppSummary = {
  name: string;
  env: Environment | null;
  region: string | null;
  machine_state: string | null;
  http_status: number | null;
  latency_ms: number | null;
  checked_at: number | null;
  hostname: string;
  autostop: "off" | "stop" | "suspend" | null;
  autostart: boolean | null;
};

export type AppsResponse = {
  apps: AppSummary[];
  pairs: Record<string, [string, string]>;
  scenario_apps: string[];
};

export type AutostopResponse = {
  app: string;
  machine_id: string;
  autostop: "off" | "stop";
  autostart: boolean;
  changed: boolean;
};

export type StatusRow = {
  ts: number;
  machine_state: string | null;
  region: string | null;
  http_status: number | null;
  latency_ms: number | null;
};

export type StatusResponse = {
  app: string;
  since_ms: number;
  rows: StatusRow[];
};

export type UptimeBucket = {
  ts: number;
  total: number;
  healthy: number;
  uptime_pct: number;
};

export type UptimeResponse = {
  app: string;
  bucket: "day" | "hour";
  since_ms: number;
  days: number;
  expected_per_bucket: number;
  buckets: UptimeBucket[];
};

export type StartSwitchResponse = {
  job_id: string;
  env: Environment;
  target_region: string;
  apps: [string, string];
};

export type SwitchEvent = {
  job_id: string;
  app: string;
  step: string;
  status: "start" | "ok" | "error" | "info";
  detail: string;
  data: Record<string, unknown>;
};

export const api = {
  me: () => req<Visitor>("/api/me"),
  apps: () => req<AppsResponse>("/api/apps"),
  status: (app: string, hours = 24) => req<StatusResponse>(`/api/status/${app}?hours=${hours}`),
  uptime: (app: string, days = 30, bucket: "day" | "hour" = "day") =>
    req<UptimeResponse>(`/api/uptime/${app}?days=${days}&bucket=${bucket}`),
  startSwitch: (body: { env: Environment; target_region: string; destroy_old?: boolean }) =>
    req<StartSwitchResponse>("/api/region/switch", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    }),
  setAutostop: (app: string, mode: "off" | "stop") =>
    req<AutostopResponse>(`/api/apps/${encodeURIComponent(app)}/autostop`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ mode }),
    }),
};

export async function streamSwitch(
  jobId: string,
  onEvent: (ev: SwitchEvent) => void,
  onEnd: () => void,
  onError: (e: Event) => void,
): Promise<() => void> {
  // EventSource can't set headers, so the JWT goes on the query string.
  const token = await getToken();
  const url = `/api/events/switch/${jobId}?token=${encodeURIComponent(token || "")}`;
  const es = new EventSource(url);
  es.addEventListener("step", (e) => {
    try {
      onEvent(JSON.parse((e as MessageEvent).data));
    } catch (err) {
      console.error("bad SSE payload", err);
    }
  });
  es.addEventListener("end", () => {
    es.close();
    onEnd();
  });
  es.addEventListener("error", (e) => onError(e));
  return () => es.close();
}
